import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Play,
    Pause,
    SkipForward,
    SkipBack,
    Lightbulb,
    Eye,
    EyeOff,
    Power,
    Timer,
    PlusCircle,
    RefreshCw,
    Sparkles,
    Square,
    Pen,
    Highlighter,
    Eraser,
    MousePointer2,
    Maximize2,
    RotateCcw,
    Wifi,
    Brain,
    BookOpen,
    Activity,
    Layers,
    AlertTriangle,
    CornerDownLeft,
    Forward,
    Rewind,
    Zap,
    HelpCircle,
    PenLine,
    PencilLine,
    PlayCircle,
    Check,
    X,
    Volume2,
    Wand2,
    BookMarked,
    ChevronDown,
    ChevronRight,
    Mic,
} from "lucide-react";
import { LessonStage } from "../components/LessonStage";
import { VoiceAssistantModal } from "../components/VoiceAssistantModal";
import { useSyncState, initialSessionState } from "../hooks/useSyncState";
import { useSessionChannel } from "../hooks/useSessionChannel";
import {
    lesson,
    defaultMascotForMode,
    slideTypeLabel,
    findSlideIdxById,
    findMcqById,
    findSubjectiveById,
    mockHandwritingDetect,
} from "../lib/lessonData";
import { setActiveLessonId } from "../lib/lessonRegistry";
import { findStoryboardEntry, loadStoredStoryboard } from "../lib/storyboard";
import TeacherPanelDeck from "./TeacherPanelDeck";
import { getBackendUrl } from "../lib/utils";

const FRAGMENT_MS = 5500;
const POST_AUDIO_PAUSE_MS = 500;

/* -------- atoms -------- */
const Pill = ({ children, tone = "neutral", className = "" }) => {
    const tones = {
        neutral: "bg-black/[0.04] text-[#4a5662]",
        primary: "bg-[#E0F4F4] text-[#2E8E9A]",
        warm: "bg-[#FFF1E5] text-[#C75730]",
        ok: "bg-[#DCEFEC] text-[#2D7A6A]",
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold ${tones[tone]} ${className}`}
        >
            {children}
        </span>
    );
};

const PrimaryBtn = ({ children, onClick, testid, className = "", disabled }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        disabled={disabled}
        className={`px-3 py-2 rounded-xl sparkl-gradient text-white font-semibold text-xs shadow-[0_4px_14px_rgba(91,194,204,0.25)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.35)] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
        {children}
    </button>
);

const SoftBtn = ({ children, onClick, testid, active = false, className = "", disabled = false }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        disabled={disabled}
        className={`px-2.5 py-2 rounded-xl bg-white border-2 font-medium text-xs active:scale-[0.97] transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${
            active
                ? "border-[#5BC2CC] text-[#14202a] bg-[#E0F4F4]"
                : "border-black/[0.06] text-[#14202a] hover:border-[#B8E8E8]"
        } ${className}`}
    >
        {children}
    </button>
);

const WarmBtn = ({ children, onClick, testid, className = "" }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`px-2.5 py-2 rounded-xl rose-gradient text-[#7A3A20] font-semibold text-xs active:scale-[0.97] transition-all duration-200 flex items-center justify-center gap-1.5 shadow-[0_4px_14px_rgba(255,154,120,0.3)] ${className}`}
    >
        {children}
    </button>
);

const Section = ({ title, children, action }) => (
    <div className="rounded-2xl bg-white border border-black/[0.04] soft-shadow overflow-hidden">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-black/[0.04]">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                {title}
            </span>
            {action}
        </div>
        <div className="p-4">{children}</div>
    </div>
);

const formatDuration = (ms) => {
    if (!ms || ms < 0) return "00:00";
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const TeacherPanel = () => {
    const [state, set, sendCommand] = useSyncState();
    const [tab, setTab] = useState("recommendations");
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);

    /* ===== Mobile / cross-device session channel =====
       The teacher web panel is the single source of truth for state. A
       mobile controller (React Native) and any other peer connect to the
       backend WebSocket relay using the same sessionId. This block:
         • Broadcasts every state change to the channel as STATE_SYNC.
         • Listens for PHOTO_CMD envelopes (from the phone) and applies
           them via sendCommand. */
    const sessionIdForChannel = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("sessionId") || "default";
    }, []);
    const sessionChannel = useSessionChannel(sessionIdForChannel);

    // Active lesson id from URL — used to tag every STATE_SYNC broadcast.
    const lessonIdForChannel = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("lessonId") || "photosynthesis";
    }, []);

    const [joinCode, setJoinCode] = useState(() => {
        try {
            return (
                sessionStorage.getItem(
                    `sparkl-join-code-${sessionIdForChannel}`,
                ) ||
                localStorage.getItem(
                    `sparkl-join-code-${sessionIdForChannel}`,
                ) ||
                null
            );
        } catch {
            return null;
        }
    });

    useEffect(() => {
        if (joinCode) return;
        const base = getBackendUrl();
        if (!base) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(
                    `${base}/axis-ai/v1/octane/sessions/${encodeURIComponent(
                        sessionIdForChannel,
                    )}/ensure`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ lesson_id: "photosynthesis" }),
                    },
                );
                if (!r.ok) return;
                const data = await r.json();
                if (cancelled) return;
                setJoinCode(data.join_code);
                try {
                    sessionStorage.setItem(
                        `sparkl-join-code-${sessionIdForChannel}`,
                        data.join_code,
                    );
                    localStorage.setItem(
                        `sparkl-join-code-${sessionIdForChannel}`,
                        data.join_code,
                    );
                } catch {
                    /* noop */
                }
            } catch {
                /* offline */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionIdForChannel, joinCode]);

    /* Push state to the channel:
       1) On every state change (debounced 120ms).
       2) On a 2s heartbeat so late-joining mobile peers always get a
          fresh snapshot within a couple seconds even if they missed the
          REQUEST_STATE/response handshake. */
    useEffect(() => {
        if (!sessionChannel?.send) return;
        const debounce = setTimeout(() => {
            sessionChannel.send({
                type: "STATE_SYNC",
                role: "teacher-web",
                lessonId: lessonIdForChannel,
                payload: state,
            });
        }, 120);
        return () => clearTimeout(debounce);
    }, [state, sessionChannel]);

    // Heartbeat — fires every 5s unconditionally so late-joining peers
    // (mobile controller, second laptop) always sync within a few
    // seconds even if they connect after the initial state-change
    // STATE_SYNC. Dedupe is now handled on the phone side, so we can
    // be generous on the wire.
    useEffect(() => {
        if (!sessionChannel?.send) return;
        const id = setInterval(() => {
            sessionChannel.send({
                type: "STATE_SYNC",
                role: "teacher-web",
                lessonId: lessonIdForChannel,
                payload: state,
                _heartbeat: true,
            });
        }, 5000);
        return () => clearInterval(id);
    }, [sessionChannel, state]);

    useEffect(() => {
        if (!sessionChannel?.onMessage) return;
        return sessionChannel.onMessage((m) => {
            if (m.type === "REQUEST_STATE") {
                // A late-joining peer (typically the mobile controller) asked
                // for the current state. Send it on the next tick so the
                // peer's WS handler is fully wired.
                setTimeout(() => {
                    sessionChannel.send({
                        type: "STATE_SYNC",
                        role: "teacher-web",
                        lessonId: lessonIdForChannel,
                        payload: state,
                    });
                }, 50);
                return;
            }
            if (m.type === "STATE_SYNC" && m.role !== "teacher-web") {
                if (m.payload) {
                    set((prev) => {
                        const hasChange = Object.keys(m.payload).some(
                            (key) => prev[key] !== m.payload[key]
                        );
                        if (!hasChange) return {};
                        return m.payload;
                    });
                }
                return;
            }
            if (m.type !== "PHOTO_CMD") return;
            // m.cmd: command name string;  m.payload: partial state to merge.
            try {
                sendCommand(m.cmd || "PHONE_CMD", m.payload || {});
            } catch {
                /* noop */
            }
        });
    }, [sessionChannel, sendCommand, set, state, lessonIdForChannel]);

    const [openSections, setOpenSections] = useState({
        concept: true,
        "concept-check": false,
        practice: false,
        activity: false,
        recap: false,
    });

    const slide = lesson.slides[state.slideIndex] || lesson.slides[0];
    const totalFragments = slide.fragments?.length || 0;

    // Auto-start the session if Classroom Mode was launched via the landing
    // page (?sessionId=... is in the URL) and the session isn't running yet.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const incoming = params.get("sessionId");
        if (!incoming) return;
        if (state.sessionStarted && state.sessionId === incoming) return;
        sendCommand("START_SESSION", {
            ...initialSessionState,
            sessionId: incoming,
            sessionStarted: true,
            sessionStartedAt: Date.now(),
            slideSelected: false,
            slideRenderMode: "play",
            autoPilotEnabled: false,
            mascotState: "idle",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [, force] = useState(0);
    useEffect(() => {
        if (!state.sessionStarted) return;
        const id = setInterval(() => force((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [state.sessionStarted]);

    const sessionElapsed = state.sessionStartedAt
        ? Date.now() - state.sessionStartedAt
        : 0;
    const isConnected =
        state.sessionStarted && Date.now() - (state.updatedAt || 0) < 60_000;

    /* ------------ session controls ------------ */
    const startSession = () =>
        sendCommand("START_SESSION", {
            ...initialSessionState,
            sessionStarted: true,
            sessionStartedAt: Date.now(),
            slideSelected: false,
            slideRenderMode: "play",
            autoPilotEnabled: false,
            mascotState: "idle",
        });
    const requestStudentFullscreen = () => {
        // Try to address the named student window opened by Classroom Mode.
        // window.open("", name) returns the existing window if it's open,
        // without navigating it. We then postMessage so the student window's
        // listener calls requestFullscreen() (it already exists in StudentDisplay).
        try {
            const w = window.open("", "sparkl-student-display");
            if (w) {
                w.postMessage({ type: "sparkl:fullscreen" }, "*");
                try {
                    w.focus();
                } catch {
                    /* noop */
                }
            }
        } catch {
            /* noop */
        }
        // BroadcastChannel fallback (in case the student window has lost its
        // opener reference — e.g. dragged to another display by the OS).
        try {
            const ch = new BroadcastChannel("sparkl-classroom-session");
            ch.postMessage({ type: "STUDENT_FULLSCREEN" });
            ch.close();
        } catch {
            /* noop */
        }
    };

    const endSession = () => {
        // Broadcast END so the student view can show the goodbye screen,
        // then take the teacher back to the home screen.
        sendCommand("END_SESSION", {
            ...initialSessionState,
            sessionEnded: true,
            sessionEndedAt: Date.now(),
        });
        // Tiny delay so the broadcast reaches the student window before we leave.
        setTimeout(() => {
            try {
                window.location.href = "/";
            } catch {
                /* noop */
            }
        }, 120);
    };
    const togglePause = () =>
        sendCommand(state.aiPaused ? "RESUME_TEACHING" : "PAUSE_TEACHING", {
            aiPaused: !state.aiPaused,
            mascotState: !state.aiPaused
                ? "idle"
                : defaultMascotForMode(slide.mode),
        });
    const toggleBlank = () =>
        sendCommand(
            state.blanked ? "RESTORE_STUDENT_SCREEN" : "BLANK_STUDENT_SCREEN",
            { blanked: !state.blanked },
        );
    const toggleAutoFlow = () =>
        sendCommand(
            state.autoFlowEnabled ? "DISABLE_AUTO_FLOW" : "ENABLE_AUTO_FLOW",
            { autoFlowEnabled: !state.autoFlowEnabled },
        );

    /* ------------ slide & fragment nav ------------ */
    const baseSlideReset = (idx) => {
        const s = lesson.slides[idx];
        return {
            slideIndex: idx,
            fragmentIndex: 0,
            mode: s.mode,
            currentSection: s.section,
            mascotState: defaultMascotForMode(s.mode),
            slideSelected: true,
            slideRenderMode: "play",
            revealAnswer: false,
            revealMcqAnswer: false,
            revealExpected: false,
            revealCommonMistake: false,
            solutionStep: 0,
            videoPlaying: false,
            timerStartedAt: null,
            timerPaused: false,
            activityResult: null,
        };
    };
    const jumpToSlide = (idx) =>
        sendCommand("GO_TO_SLIDE", baseSlideReset(idx));
    const jumpToSlideById = (id) => {
        const i = findSlideIdxById(lesson.slides, id);
        if (i >= 0) jumpToSlide(i);
    };
    const nextSlide = () => {
        if (state.slideIndex < lesson.slides.length - 1)
            sendCommand("GO_TO_NEXT_SLIDE", baseSlideReset(state.slideIndex + 1));
    };
    const prevSlide = () => {
        if (state.slideIndex > 0)
            sendCommand("GO_TO_PREVIOUS_SLIDE", baseSlideReset(state.slideIndex - 1));
    };
    const nextFragment = () => {
        if (state.fragmentIndex < totalFragments - 1)
            sendCommand("GO_TO_NEXT_FRAGMENT", {
                fragmentIndex: state.fragmentIndex + 1,
            });
    };
    const prevFragment = () => {
        if (state.fragmentIndex > 0)
            sendCommand("GO_TO_PREVIOUS_FRAGMENT", {
                fragmentIndex: state.fragmentIndex - 1,
            });
    };
    const replaySlide = () =>
        sendCommand("REPLAY_AI_NARRATION", { fragmentIndex: 0 });

    /* ------------ MCQ ------------ */
    const showMcq = (mcqId) => {
        const idx = findSlideIdxById(lesson.slides, "mcq-display");
        if (idx < 0) return;
        const reset = baseSlideReset(idx);
        sendCommand("SHOW_MCQ", (prev) => ({
            ...reset,
            activeMcqId: mcqId,
            returnToSlideIdx:
                prev.slideIndex === idx ? prev.returnToSlideIdx : prev.slideIndex,
        }));
    };
    const revealMcqAnswer = () =>
        sendCommand("REVEAL_MCQ_ANSWER", { revealMcqAnswer: true });
    const hideMcqAnswer = () =>
        sendCommand("HIDE_MCQ_ANSWER", { revealMcqAnswer: false });

    /* ------------ Subjective ------------ */
    const applyAsSolved = (qId) => {
        const idx = findSlideIdxById(lesson.slides, "subjective-display");
        if (idx < 0) return;
        const reset = baseSlideReset(idx);
        sendCommand("USE_QUESTION_AS_SOLVED_EXAMPLE", (prev) => ({
            ...reset,
            activeSubjectiveId: qId,
            subjectiveMode: "solved",
            solutionStep: 0,
            returnToSlideIdx:
                prev.slideIndex === idx ? prev.returnToSlideIdx : prev.slideIndex,
        }));
    };
    const assignAsPractice = (qId) => {
        const idx = findSlideIdxById(lesson.slides, "subjective-display");
        if (idx < 0) return;
        const reset = baseSlideReset(idx);
        sendCommand("ASSIGN_QUESTION_AS_PRACTICE", (prev) => ({
            ...reset,
            activeSubjectiveId: qId,
            subjectiveMode: "practice",
            returnToSlideIdx:
                prev.slideIndex === idx ? prev.returnToSlideIdx : prev.slideIndex,
        }));
    };
    const nextSolutionStep = () =>
        sendCommand("REVEAL_SOLUTION_STEP", (p) => {
            const q = findSubjectiveById(p.activeSubjectiveId);
            const max = (q?.steps?.length || 1) - 1;
            return { solutionStep: Math.min(max, (p.solutionStep || 0) + 1) };
        });
    const showExpected = () =>
        sendCommand("SHOW_EXPECTED_ANSWER", { revealExpected: true });
    const showCommonMistake = () =>
        sendCommand("SHOW_COMMON_MISTAKE", { revealCommonMistake: true });

    /* ------------ Activity ------------ */
    const startDragDrop = () => {
        const idx = findSlideIdxById(lesson.slides, "drag-drop");
        if (idx < 0) return;
        const reset = baseSlideReset(idx);
        sendCommand("START_DRAG_DROP_ACTIVITY", (prev) => ({
            ...reset,
            activeActivityId: lesson.activity.id,
            returnToSlideIdx:
                prev.slideIndex === idx ? prev.returnToSlideIdx : prev.slideIndex,
        }));
    };
    const showActivityAnswer = () => {
        // Auto-place every item in its correct slot, mark all correct
        const placements = {};
        const checks = {};
        lesson.activity.items.forEach((it) => {
            if (it.correct) {
                placements[it.id] = it.correct;
                checks[it.id] = "correct";
            }
        });
        sendCommand("SHOW_ACTIVITY_ANSWER", {
            dragPlacements: placements,
            dragCheckResult: {
                correct: Object.keys(checks).length,
                total: lesson.activity.items.filter((i) => i.correct).length,
                checks,
            },
            activityResult: "reveal",
        });
    };
    const resetActivity = () =>
        sendCommand("RESET_ACTIVITY", {
            activityResult: null,
            dragPlacements: {},
            dragCheckResult: null,
        });
    const checkDragDrop = () =>
        sendCommand("CHECK_DRAG_DROP_ANSWER", (p) => {
            const placements = p.dragPlacements || {};
            const checks = {};
            let correct = 0;
            let total = 0;
            lesson.activity.items.forEach((it) => {
                if (it.correct === null) {
                    // distractor — correct answer is "leave in bank"
                    total++;
                    if (!placements[it.id]) {
                        correct++;
                    } else {
                        checks[it.id] = "wrong";
                    }
                } else {
                    total++;
                    if (placements[it.id] === it.correct) {
                        checks[it.id] = "correct";
                        correct++;
                    } else if (placements[it.id]) {
                        checks[it.id] = "wrong";
                    }
                }
            });
            return { dragCheckResult: { correct, total, checks } };
        });
    const placeItem = (itemId, catId) =>
        set((p) => ({
            dragPlacements: { ...(p.dragPlacements || {}), [itemId]: catId },
            dragCheckResult: null,
        }));
    const pickupItem = (itemId) =>
        set((p) => {
            const next = { ...(p.dragPlacements || {}) };
            delete next[itemId];
            return { dragPlacements: next, dragCheckResult: null };
        });

    /* ------------ Timer ------------ */
    const startTimer = () => {
        const q = findSubjectiveById(state.activeSubjectiveId);
        const seconds = q?.estimatedSeconds || slide.timerSeconds || 60;
        sendCommand("START_TIMER", {
            timerStartedAt: Date.now(),
            timerSeconds: seconds,
            timerPaused: false,
        });
    };
    const extendTimer = () =>
        sendCommand("EXTEND_TIMER", (p) => ({
            timerSeconds: (p.timerSeconds || 0) + 30,
        }));
    const stopTimer = () =>
        sendCommand("END_ACTIVITY", { timerStartedAt: null, timerPaused: false });

    /* ------------ Blank slide ------------ */
    const addBlankSlide = (type = "white") => {
        const idx = findSlideIdxById(lesson.slides, "blank-slide");
        if (idx < 0) return;
        const reset = baseSlideReset(idx);
        sendCommand("SHOW_BLANK_SLIDE", (prev) => ({
            ...reset,
            blankSlideActive: true,
            blankSlideType: type,
            aiHandwritingResult: null,
            aiHelpShowToStudents: false,
            returnToSlideIdx:
                prev.slideIndex === idx ? prev.returnToSlideIdx : prev.slideIndex,
        }));
    };
    const readHandwriting = () =>
        sendCommand("READ_HANDWRITING", {
            aiHandwritingResult: mockHandwritingDetect(),
        });
    const showAiCleaned = () =>
        sendCommand("SHOW_AI_EXPLANATION_TO_STUDENTS", {
            aiHelpShowToStudents: true,
        });

    /* ------------ Annotations ------------ */
    const setTool = (tool) => set({ annotationTool: tool });
    const setColor = (color) => set({ annotationColor: color });
    const addStroke = (stroke) =>
        set((p) => {
            const id = lesson.slides[p.slideIndex].id;
            const prev = p.annotations?.[id] || [];
            return { annotations: { ...p.annotations, [id]: [...prev, stroke] } };
        });
    const undoStroke = () =>
        set((p) => {
            const id = lesson.slides[p.slideIndex].id;
            const prev = p.annotations?.[id] || [];
            return {
                annotations: { ...p.annotations, [id]: prev.slice(0, -1) },
            };
        });
    const clearAnnotations = () =>
        set((p) => {
            const id = lesson.slides[p.slideIndex].id;
            return { annotations: { ...p.annotations, [id]: [] } };
        });

    /* ------------ Recommendations ------------ */
    const dismissRec = (id) =>
        sendCommand("DISMISS_RECOMMENDATION", (p) => ({
            dismissedRecommendations: [
                ...(p.dismissedRecommendations || []),
                id,
            ],
        }));
    const [previewExampleId, setPreviewExampleId] = useState(null);
    const previewExample = previewExampleId
        ? lesson.realLifeExamples.find((e) => e.id === previewExampleId)
        : null;

    const showExampleToStudents = (exId) => {
        sendCommand("SHOW_EXAMPLE_POPUP_TO_STUDENTS", { examplePopup: exId });
        setPreviewExampleId(null);
    };
    const closeExamplePopupForStudents = () =>
        sendCommand("CLOSE_EXAMPLE_POPUP", { examplePopup: null });

    // legacy showExample (used by recs that say action: "show-example") — open teacher preview
    const showExample = (exId) => setPreviewExampleId(exId);
    const showHelp = (conceptKey) =>
        sendCommand("OPEN_AI_HELP", {
            aiHelpOpen: true,
            aiHelpConcept: conceptKey,
        });

    /* ------------ Auto-flow ------------
       Fragments auto-advance ONLY while in 'play' render mode.
       Timing is driven by the storyboard audio when available:
         wait = audio_duration + POST_AUDIO_PAUSE_MS (default 500ms)
       Falls back to FRAGMENT_MS when no audio is uploaded.
       At last fragment, advance to next concept slide ONLY if auto-pilot is on.
    */
    const [storyboard, setStoryboard] = useState(() => loadStoredStoryboard());
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key && e.key.startsWith("sparkl-storyboard-")) {
                setStoryboard(loadStoredStoryboard());
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const measureAudioDurationMs = (src) =>
        new Promise((resolve) => {
            try {
                const probe = new Audio();
                let settled = false;
                const finish = (ms) => {
                    if (settled) return;
                    settled = true;
                    resolve(ms);
                };
                probe.addEventListener("loadedmetadata", () => {
                    const sec = probe.duration;
                    if (sec && isFinite(sec) && sec > 0) {
                        finish(Math.round(sec * 1000));
                    } else {
                        finish(FRAGMENT_MS);
                    }
                });
                probe.addEventListener("error", () => finish(FRAGMENT_MS));
                // safety timeout
                setTimeout(() => finish(FRAGMENT_MS), 15000);
                probe.preload = "metadata";
                probe.src = src;
            } catch {
                resolve(FRAGMENT_MS);
            }
        });

    const lastTimerKey = useRef("");
    useEffect(() => {
        if (!state.sessionStarted) return;
        if (state.slideSelected === false) return;
        if (state.aiPaused || state.blanked) return;
        if (state.slideRenderMode === "full") return;

        // Heartbeat coordination: if a student display is active and driving transitions, do NOT schedule timers here.
        const isSmartboardActive = state.smartboardActive && (Date.now() - state.smartboardActive < 8000);
        if (isSmartboardActive) return;

        let timerId = null;
        let cancelled = false;
        const sched = (ms, fn) => {
            timerId = setTimeout(() => {
                if (!cancelled) fn();
            }, ms);
        };

        // Determine wait duration: audio + 500 ms, else FRAGMENT_MS.
        const computeWait = async () => {
            const entry = findStoryboardEntry(
                storyboard,
                state.slideIndex,
                state.fragmentIndex,
            );
            if (entry?.audio) {
                if (entry.durationMs !== undefined && entry.durationMs !== null) {
                    return entry.durationMs + POST_AUDIO_PAUSE_MS;
                }
                const ms = await measureAudioDurationMs(entry.audio);
                return ms + POST_AUDIO_PAUSE_MS;
            }
            return FRAGMENT_MS;
        };

        // 1) advance fragment if not at last
        if (state.fragmentIndex < totalFragments - 1) {
            const key = `${state.slideIndex}:${state.fragmentIndex}`;
            if (lastTimerKey.current === key) return;
            lastTimerKey.current = key;

            computeWait().then((ms) => {
                if (cancelled) return;
                sched(ms, () => {
                    set((p) => {
                        if (p.slideIndex !== state.slideIndex) return {};
                        const total =
                            lesson.slides[p.slideIndex].fragments?.length || 0;
                        if (p.fragmentIndex >= total - 1) return {};
                        return { fragmentIndex: p.fragmentIndex + 1 };
                    });
                });
            });
            return () => {
                cancelled = true;
                if (timerId) clearTimeout(timerId);
            };
        }

        // 2) at last fragment → advance to next slide ONLY if auto-pilot on
        if (!state.autoPilotEnabled) return;
        const inConcept = lesson.slides[state.slideIndex].section === "concept";
        if (!inConcept) return;
        if (lesson.slides[state.slideIndex].mode === "video_teaching") return;
        const conceptIds = lesson.sections.find(
            (s) => s.id === "concept",
        )?.slideIds || [];
        const currentId = lesson.slides[state.slideIndex].id;
        const idxInConcept = conceptIds.indexOf(currentId);
        if (idxInConcept < 0 || idxInConcept >= conceptIds.length - 1) return;

        const key = `next-slide:${state.slideIndex}`;
        if (lastTimerKey.current === key) return;
        lastTimerKey.current = key;

        computeWait().then((ms) => {
            if (cancelled) return;
            sched(ms, () => {
                set((p) => {
                    if (p.slideIndex !== state.slideIndex) return {};
                    const nextId = conceptIds[idxInConcept + 1];
                    const nextIdx = findSlideIdxById(lesson.slides, nextId);
                    if (nextIdx < 0) return {};
                    const ns = lesson.slides[nextIdx];
                    return {
                        slideIndex: nextIdx,
                        fragmentIndex: 0,
                        mode: ns.mode,
                        currentSection: "concept",
                        mascotState: defaultMascotForMode(ns.mode),
                        slideRenderMode: "play",
                    };
                });
            });
        });
        return () => {
            cancelled = true;
            if (timerId) clearTimeout(timerId);
        };
    }, [
        state.sessionStarted,
        state.slideSelected,
        state.aiPaused,
        state.blanked,
        state.autoPilotEnabled,
        state.slideRenderMode,
        state.slideIndex,
        state.fragmentIndex,
        state.smartboardActive,
        totalFragments,
        storyboard,
        set,
    ]);

    /* ------------ Solved-example step auto-advance ------------ */
    const SOLVED_STEP_MS = 6000;
    const lastSolvedKey = useRef("");
    useEffect(() => {
        if (!state.sessionStarted) return;
        if (state.aiPaused || state.blanked) return;
        if (slide.mode !== "subjective_display") return;
        if (state.subjectiveMode !== "solved") return;
        const q = findSubjectiveById(state.activeSubjectiveId);
        const max = (q?.steps?.length || 1) - 1;
        if ((state.solutionStep || 0) >= max) return;
        const key = `${state.activeSubjectiveId}:${state.solutionStep}`;
        if (lastSolvedKey.current === key) return;
        lastSolvedKey.current = key;
        const id = setTimeout(() => {
            set((p) => {
                if (p.subjectiveMode !== "solved") return {};
                if (p.activeSubjectiveId !== state.activeSubjectiveId) return {};
                return {
                    solutionStep: Math.min(max, (p.solutionStep || 0) + 1),
                };
            });
        }, SOLVED_STEP_MS);
        return () => clearTimeout(id);
    }, [
        state.sessionStarted,
        state.aiPaused,
        state.blanked,
        state.subjectiveMode,
        state.activeSubjectiveId,
        state.solutionStep,
        slide.mode,
        set,
    ]);

    /* ------------ Recommendation: 1-2 highlighted recs for the entire lesson ------------ */
    // Only show the floating AI suggestion on a small handful of slides.
    // Picks the FIRST recommendation for each of these slides.
    const FEATURED_REC_SLIDES = ["sunlight", "drag-drop"];
    const recsForSlide = useMemo(() => {
        if (!FEATURED_REC_SLIDES.includes(slide.id)) return [];
        const all = lesson.aiRecommendations[slide.id] || [];
        return all
            .filter((r) => !state.dismissedRecommendations?.includes(r.id))
            .slice(0, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slide.id, state.dismissedRecommendations]);
    const activeRec = recsForSlide[0] || null;

    /* ------------ Tabs (defined inline, share closures) ------------ */
    const RecsTab = () => (
        <div className="space-y-4">
            <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2 flex items-center gap-1.5">
                    <Brain className="w-3 h-3" />
                    Live recommendations for "{slide.title}"
                </p>
                {recsForSlide.length === 0 ? (
                    <p className="text-xs text-[#4a5662] italic">
                        No active recommendations.
                    </p>
                ) : (
                    <div className="space-y-2.5">
                        {recsForSlide.map((r) => (
                            <RecCard key={r.id} r={r} />
                        ))}
                    </div>
                )}
            </div>

            <div className="border-t border-black/[0.05] pt-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2 flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" />
                    Real-life examples
                </p>
                <div className="space-y-2">
                    {lesson.realLifeExamples.map((e) => (
                        <div
                            key={e.id}
                            className="p-3 rounded-xl bg-[#FFF1E5]/60 border border-[#FFA987]/20 flex items-start gap-2"
                        >
                            <p className="flex-1 text-xs text-[#14202a] leading-relaxed">
                                {e.text}
                            </p>
                            <SoftBtn
                                testid={`teacher-show-example-${e.id}`}
                                onClick={() => showExample(e.id)}
                                className="!px-2"
                            >
                                <Eye className="w-3 h-3" />
                            </SoftBtn>
                        </div>
                    ))}
                </div>
            </div>

            <div className="border-t border-black/[0.05] pt-4">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2 flex items-center gap-1.5">
                    <Wand2 className="w-3 h-3" />
                    Help me teach
                </p>
                <div className="space-y-2">
                    {Object.keys(lesson.teacherHelp).map((k) => (
                        <SoftBtn
                            key={k}
                            testid={`teacher-help-${k}-btn`}
                            onClick={() => showHelp(k)}
                            className="!justify-start w-full !text-xs capitalize"
                        >
                            <BookMarked className="w-3 h-3" />
                            {k.replace(/-/g, " ")}
                        </SoftBtn>
                    ))}
                </div>
                {state.aiHelpOpen && state.aiHelpConcept && (
                    <HelpPanel />
                )}
            </div>
        </div>
    );

    const RecCard = ({ r, compact = false }) => {
        const handle = () => {
            if (r.action === "show-mcq") showMcq(r.target);
            else if (r.action === "use-solved") applyAsSolved(r.target);
            else if (r.action === "assign-practice") assignAsPractice(r.target);
            else if (r.action === "start-activity") startDragDrop();
            else if (r.action === "show-example") showExample(r.target);
            else if (r.action === "show-help") showHelp(r.target);
            else if (r.action === "annotate") setTool("pen");
            else if (r.action === "pause") togglePause();
            else if (r.action === "show-activity-answer") showActivityAnswer();
        };
        if (compact) {
            return (
                <div className="flex flex-wrap gap-1.5">
                    <PrimaryBtn
                        testid={`rec-show-${r.id}`}
                        onClick={handle}
                        className="!px-2 !py-0.5 !text-[10px]"
                    >
                        Show this
                    </PrimaryBtn>
                    <SoftBtn
                        testid={`rec-dismiss-${r.id}`}
                        onClick={() => dismissRec(r.id)}
                        className="!px-2 !py-0.5 !text-[10px]"
                    >
                        <X className="w-2.5 h-2.5" />
                        Dismiss
                    </SoftBtn>
                </div>
            );
        }
        return (
            <div className="p-3 rounded-xl bg-[#E0F4F4]/60 border border-[#5BC2CC]/25 flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg sparkl-gradient flex items-center justify-center shrink-0">
                    <Sparkles className="w-3 h-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#14202a] leading-snug mb-2">
                        {r.text}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        <PrimaryBtn
                            testid={`rec-show-${r.id}`}
                            onClick={handle}
                            className="!px-2 !py-1 !text-[10px]"
                        >
                            Show this
                        </PrimaryBtn>
                        <SoftBtn
                            testid={`rec-dismiss-${r.id}`}
                            onClick={() => dismissRec(r.id)}
                            className="!px-2 !py-1 !text-[10px]"
                        >
                            <X className="w-2.5 h-2.5" />
                            Dismiss
                        </SoftBtn>
                    </div>
                </div>
            </div>
        );
    };

    const HelpPanel = () => {
        const c = lesson.teacherHelp[state.aiHelpConcept];
        if (!c) return null;
        return (
            <div className="mt-3 p-4 rounded-xl bg-white border border-[#5BC2CC]/30">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#2E8E9A] font-semibold capitalize">
                        AI help · {state.aiHelpConcept.replace(/-/g, " ")}
                    </p>
                    <button
                        onClick={() => set({ aiHelpOpen: false })}
                        data-testid="teacher-help-close-btn"
                        className="text-[#4a5662] hover:text-[#14202a]"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="space-y-3 text-xs">
                    <div>
                        <p className="text-[#4a5662] font-semibold mb-1">
                            Simple explanation
                        </p>
                        <p className="text-[#14202a] leading-relaxed">
                            {c.simple}
                        </p>
                    </div>
                    <div>
                        <p className="text-[#4a5662] font-semibold mb-1">
                            Probing question
                        </p>
                        <p className="text-[#14202a] leading-relaxed">
                            {c.probing}
                        </p>
                    </div>
                    {c.misconception && (
                        <div className="p-2.5 rounded-lg bg-[#FFF1E5] border border-[#FFA987]/30">
                            <p className="text-[#C75730] font-semibold mb-1 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Misconception
                            </p>
                            <p className="text-[#14202a] leading-relaxed">
                                {c.misconception}
                            </p>
                        </div>
                    )}
                    {c.analogy && (
                        <div>
                            <p className="text-[#4a5662] font-semibold mb-1">
                                Analogy
                            </p>
                            <p className="text-[#14202a] italic">{c.analogy}</p>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 pt-2">
                        <PrimaryBtn
                            testid="teacher-help-narrate-btn"
                            onClick={() =>
                                sendCommand("USE_AI_NARRATION", {
                                    captionOverride: c.simple,
                                })
                            }
                        >
                            <Volume2 className="w-3 h-3" />
                            Use as narration
                        </PrimaryBtn>
                    </div>
                </div>
            </div>
        );
    };

    const McqTab = () => (
        <div className="space-y-3">
            {lesson.mcqs.map((m, i) => (
                <div
                    key={m.id}
                    className={`p-3.5 rounded-xl border ${
                        state.activeMcqId === m.id
                            ? "border-[#5BC2CC] bg-[#E0F4F4]/40"
                            : "border-black/[0.05] bg-white"
                    }`}
                >
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Pill tone="primary">MCQ Q{i + 1}</Pill>
                        <Pill tone="neutral">{m.difficulty}</Pill>
                        <Pill tone="neutral">{m.tag}</Pill>
                        <span className="text-[10px] text-[#4a5662] ml-auto">
                            {m.estimatedSeconds}s
                        </span>
                    </div>
                    <p className="text-sm font-semibold text-[#14202a] mb-3">
                        {m.question}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        <PrimaryBtn
                            testid={`teacher-show-mcq-${m.id}`}
                            onClick={() => showMcq(m.id)}
                        >
                            <Eye className="w-3 h-3" />
                            Show on display
                        </PrimaryBtn>
                        {state.activeMcqId === m.id && (
                            <>
                                {!state.revealMcqAnswer ? (
                                    <WarmBtn
                                        testid={`teacher-reveal-mcq-${m.id}`}
                                        onClick={revealMcqAnswer}
                                    >
                                        <Lightbulb className="w-3 h-3" />
                                        Reveal answer
                                    </WarmBtn>
                                ) : (
                                    <SoftBtn
                                        testid={`teacher-hide-mcq-${m.id}`}
                                        onClick={hideMcqAnswer}
                                    >
                                        <EyeOff className="w-3 h-3" />
                                        Hide answer
                                    </SoftBtn>
                                )}
                            </>
                        )}
                        <SoftBtn
                            testid={`teacher-mcq-explain-${m.id}`}
                            onClick={() =>
                                sendCommand("ASK_AI_TO_EXPLAIN_MCQ", {
                                    activeMcqId: m.id,
                                    captionOverride: m.explanation,
                                    revealMcqAnswer: true,
                                })
                            }
                        >
                            <Brain className="w-3 h-3" />
                            Ask AI
                        </SoftBtn>
                        {m.relatedConceptId && (
                            <SoftBtn
                                testid={`teacher-mcq-concept-${m.id}`}
                                onClick={() => jumpToSlideById(m.relatedConceptId)}
                            >
                                <Layers className="w-3 h-3" />
                                Concept
                            </SoftBtn>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    const SubjectiveTab = () => (
        <div className="space-y-3">
            {lesson.subjectives.map((q, i) => (
                <div
                    key={q.id}
                    className={`p-3.5 rounded-xl border ${
                        state.activeSubjectiveId === q.id
                            ? "border-[#5BC2CC] bg-[#E0F4F4]/40"
                            : "border-black/[0.05] bg-white"
                    }`}
                >
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Pill tone="primary">Q{i + 1}</Pill>
                        <Pill tone="neutral">{q.difficulty}</Pill>
                        <Pill tone="neutral">{q.tag}</Pill>
                        <span className="text-[10px] text-[#4a5662] ml-auto">
                            {Math.round(q.estimatedSeconds / 60)}m
                        </span>
                    </div>
                    <p className="text-sm font-semibold text-[#14202a] mb-3">
                        {q.question}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        <PrimaryBtn
                            testid={`teacher-use-solved-${q.id}`}
                            onClick={() => applyAsSolved(q.id)}
                        >
                            <PenLine className="w-3 h-3" />
                            Use as Solved Example
                        </PrimaryBtn>
                        <WarmBtn
                            testid={`teacher-assign-practice-${q.id}`}
                            onClick={() => assignAsPractice(q.id)}
                        >
                            <PencilLine className="w-3 h-3" />
                            Assign as Practice
                        </WarmBtn>
                        {q.relatedConceptId && (
                            <SoftBtn
                                testid={`teacher-sub-concept-${q.id}`}
                                onClick={() => jumpToSlideById(q.relatedConceptId)}
                            >
                                <Layers className="w-3 h-3" />
                                Concept
                            </SoftBtn>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    const ActivityTab = () => {
        const a = lesson.activity;
        return (
            <div className="space-y-4">
                <div className="p-4 rounded-xl bg-white border border-black/[0.05]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Pill tone="warm">{a.difficulty}</Pill>
                        <Pill tone="neutral">{Math.round(a.duration / 60)}m</Pill>
                    </div>
                    <p className="text-sm font-semibold text-[#14202a] mb-1">
                        {a.title}
                    </p>
                    <p className="text-xs text-[#4a5662] mb-3 leading-relaxed">
                        {a.objective}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        <WarmBtn
                            testid="teacher-start-dragdrop-btn"
                            onClick={startDragDrop}
                        >
                            <PlayCircle className="w-3 h-3" />
                            Start activity
                        </WarmBtn>
                        <SoftBtn
                            testid="teacher-show-activity-answer-btn"
                            onClick={showActivityAnswer}
                        >
                            <Check className="w-3 h-3" />
                            Show correct
                        </SoftBtn>
                        <SoftBtn
                            testid="teacher-reset-activity-btn"
                            onClick={resetActivity}
                        >
                            <RotateCcw className="w-3 h-3" />
                            Reset
                        </SoftBtn>
                        <SoftBtn
                            testid="teacher-activity-explain-btn"
                            onClick={() =>
                                sendCommand("ASK_AI_TO_EXPLAIN_ACTIVITY", {
                                    captionOverride:
                                        "Inputs are what plants take in: sunlight, CO₂, water. Outputs are what they produce: glucose and oxygen.",
                                })
                            }
                        >
                            <Brain className="w-3 h-3" />
                            Ask AI
                        </SoftBtn>
                    </div>
                </div>
            </div>
        );
    };

    const BlankTab = () => (
        <div className="space-y-4">
            <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                    Add blank teaching slide
                </p>
                <div className="grid grid-cols-2 gap-2">
                    {lesson.blankSlideTypes.map((t) => (
                        <SoftBtn
                            key={t.id}
                            testid={`teacher-add-blank-${t.id}`}
                            onClick={() => addBlankSlide(t.id)}
                            active={
                                slide.id === "blank-slide" &&
                                state.blankSlideType === t.id
                            }
                        >
                            <PenLine className="w-3 h-3" />
                            {t.label}
                        </SoftBtn>
                    ))}
                </div>
            </div>

            {slide.id === "blank-slide" && (
                <div className="p-4 rounded-xl bg-[#E0F4F4]/40 border border-[#5BC2CC]/25">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#2E8E9A] font-semibold mb-3">
                        AI handwriting reader
                    </p>
                    {!state.aiHandwritingResult ? (
                        <div>
                            <p className="text-xs text-[#4a5662] mb-3 leading-relaxed">
                                Draw on the preview using the pen, then ask AI
                                to read what you wrote.
                            </p>
                            <PrimaryBtn
                                testid="teacher-read-handwriting-btn"
                                onClick={readHandwriting}
                            >
                                <Wand2 className="w-3 h-3" />
                                AI read handwriting
                            </PrimaryBtn>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-1">
                                    Detected
                                </p>
                                <p className="text-xs text-[#14202a] font-mono p-2 rounded-md bg-white">
                                    {state.aiHandwritingResult.detected}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-1">
                                    Suggestion
                                </p>
                                <p className="text-xs text-[#14202a] leading-relaxed">
                                    {state.aiHandwritingResult.suggestion}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-1">
                                    Cleaned version
                                </p>
                                <p className="text-xs text-[#14202a] font-mono p-2 rounded-md bg-white">
                                    {state.aiHandwritingResult.cleanedVersion}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <PrimaryBtn
                                    testid="teacher-show-cleaned-btn"
                                    onClick={showAiCleaned}
                                >
                                    <Eye className="w-3 h-3" />
                                    Show to students
                                </PrimaryBtn>
                                <SoftBtn
                                    testid="teacher-clear-handwriting-btn"
                                    onClick={() =>
                                        set({
                                            aiHandwritingResult: null,
                                            aiHelpShowToStudents: false,
                                        })
                                    }
                                >
                                    <X className="w-3 h-3" />
                                    Clear
                                </SoftBtn>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const tabs = [
        { id: "recommendations", label: "AI", icon: Brain, render: RecsTab },
        { id: "mcqs", label: "MCQs", icon: HelpCircle, render: McqTab },
        { id: "practice", label: "Practice", icon: BookOpen, render: SubjectiveTab },
        { id: "activity", label: "Activity", icon: Activity, render: ActivityTab },
        { id: "blank", label: "Blank slide", icon: PenLine, render: BlankTab },
    ];
    const ActiveRender = tabs.find((t) => t.id === tab)?.render || RecsTab;

    /* ------------ Real-life examples grid (cards below the slide preview) ------------ */
    const RealLifeExamplesGrid = () => (
        <div className="rounded-3xl bg-white border border-black/[0.05] soft-shadow p-5">
            <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#FF9A78]" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#14202a] font-semibold">
                    Real-life examples
                </p>
                <span className="text-[10px] text-[#4a5662]">
                    Click a card to preview, then show students as a bonus pop-up.
                </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {lesson.realLifeExamples.map((e) => (
                    <button
                        key={e.id}
                        data-testid={`teacher-realife-card-${e.id}`}
                        onClick={() => setPreviewExampleId(e.id)}
                        className="text-left rounded-2xl bg-white border border-black/[0.05] hover:border-[#FFA987] overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(255,154,120,0.18)]"
                    >
                        <div className="aspect-[4/3] overflow-hidden">
                            <img
                                src={e.image}
                                alt={e.headline || e.text}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-3">
                            <p className="text-[12px] font-semibold tracking-tight text-[#14202a] mb-1">
                                {e.headline || e.text}
                            </p>
                            <p className="text-[10px] text-[#4a5662] leading-snug line-clamp-2">
                                {e.description || e.text}
                            </p>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );

    /* ------------ Slide-specific action bar (below preview) ------------ */
    const SlideActionBar = () => {
        const showReturn = state.returnToSlideIdx != null;
        const isVideo = slide.mode === "video_teaching";
        const isMcq = slide.mode === "mcq_display";
        const isSolved =
            slide.mode === "subjective_display" &&
            state.subjectiveMode === "solved";
        const isPractice =
            slide.mode === "subjective_display" &&
            state.subjectiveMode === "practice";
        const isDragDrop = slide.mode === "drag_drop";

        const hasActions =
            isVideo || isMcq || isSolved || isPractice || isDragDrop || showReturn;

        return (
            <div className="px-4 py-2.5 border-t border-black/[0.04] bg-[#FAFCFD] flex items-center gap-1.5 flex-wrap min-h-[52px]">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mr-2">
                    Slide actions
                </span>
                {!hasActions && (
                    <span className="text-[11px] text-[#4a5662] italic">
                        No specific actions for this slide.
                    </span>
                )}
                {isVideo && (
                    <>
                        <PrimaryBtn
                            testid="teacher-toggle-video-btn"
                            onClick={() =>
                                sendCommand(
                                    state.videoPlaying
                                        ? "PAUSE_VIDEO"
                                        : "PLAY_VIDEO",
                                    { videoPlaying: !state.videoPlaying },
                                )
                            }
                        >
                            {state.videoPlaying ? (
                                <>
                                    <Pause className="w-3.5 h-3.5" />
                                    Pause video
                                </>
                            ) : (
                                <>
                                    <Play className="w-3.5 h-3.5" />
                                    Play video
                                </>
                            )}
                        </PrimaryBtn>
                        <SoftBtn
                            testid="teacher-restart-video-btn"
                            onClick={() =>
                                sendCommand("RESTART_VIDEO", {
                                    videoPlaying: false,
                                    videoRestartAt: Date.now(),
                                })
                            }
                        >
                            <RefreshCw className="w-3 h-3" />
                            Restart
                        </SoftBtn>
                    </>
                )}
                {isMcq && (
                    <>
                        <WarmBtn
                            testid="teacher-reveal-answer-btn"
                            onClick={revealMcqAnswer}
                        >
                            <Lightbulb className="w-3.5 h-3.5" />
                            Reveal answer
                        </WarmBtn>
                        <SoftBtn
                            testid="teacher-hide-answer-btn"
                            onClick={hideMcqAnswer}
                        >
                            <EyeOff className="w-3 h-3" />
                            Hide
                        </SoftBtn>
                    </>
                )}
                {isSolved && (
                    <PrimaryBtn
                        testid="teacher-next-solution-step-btn"
                        onClick={nextSolutionStep}
                    >
                        <Forward className="w-3.5 h-3.5" />
                        Next step
                    </PrimaryBtn>
                )}
                {isPractice && (
                    <>
                        <WarmBtn
                            testid="teacher-start-timer-btn"
                            onClick={startTimer}
                        >
                            <Timer className="w-3.5 h-3.5" />
                            Start timer
                        </WarmBtn>
                        <SoftBtn
                            testid="teacher-extend-timer-btn"
                            onClick={extendTimer}
                        >
                            <PlusCircle className="w-3 h-3" />
                            +30s
                        </SoftBtn>
                        <SoftBtn
                            testid="teacher-stop-timer-btn"
                            onClick={stopTimer}
                        >
                            <Square className="w-3 h-3" />
                            Stop
                        </SoftBtn>
                        <SoftBtn
                            testid="teacher-show-expected-btn"
                            onClick={showExpected}
                        >
                            <Eye className="w-3 h-3" />
                            Show expected
                        </SoftBtn>
                    </>
                )}
                {isDragDrop && (
                    <>
                        <PrimaryBtn
                            testid="teacher-check-dragdrop-btn"
                            onClick={checkDragDrop}
                        >
                            <Check className="w-3.5 h-3.5" />
                            Check answer
                        </PrimaryBtn>
                        <WarmBtn
                            testid="teacher-show-correct-btn"
                            onClick={showActivityAnswer}
                        >
                            <Eye className="w-3.5 h-3.5" />
                            Show correct
                        </WarmBtn>
                        <SoftBtn
                            testid="teacher-reset-dragdrop-btn"
                            onClick={resetActivity}
                        >
                            <RotateCcw className="w-3 h-3" />
                            Reset
                        </SoftBtn>
                    </>
                )}
                {showReturn && (
                    <SoftBtn
                        testid="teacher-return-to-lesson-btn"
                        onClick={() =>
                            sendCommand("GO_TO_SLIDE", {
                                ...baseSlideReset(state.returnToSlideIdx),
                                returnToSlideIdx: null,
                                activeExampleId: null,
                            })
                        }
                        className="ml-auto"
                    >
                        <CornerDownLeft className="w-3 h-3" />
                        Return
                    </SoftBtn>
                )}
            </div>
        );
    };

    /* ------------ Section navigator card builder ------------ */
    const helpKeyForSlide = {
        "what-is": "what",
        ingredients: "ingredients",
        sunlight: "sunlight",
        chlorophyll: "chlorophyll",
        equation: "equation",
    };
    const buildSlideCard = (s, i, currentIdx) => {
        const status =
            i < currentIdx
                ? "completed"
                : i === currentIdx
                  ? "current"
                  : "upcoming";
        const expanded = i === currentIdx;
        const helpKey = helpKeyForSlide[s.id];
        const help = helpKey ? lesson.teacherHelp[helpKey] : null;
        return (
            <div
                key={s.id}
                className={`rounded-lg border transition-all overflow-hidden ${
                    status === "current"
                        ? "border-[#5BC2CC] bg-white"
                        : "border-black/[0.05] bg-white hover:border-[#B8E8E8]"
                }`}
            >
                <button
                    data-testid={`teacher-jump-slide-${i}`}
                    onClick={() => jumpToSlide(i)}
                    className="text-left w-full p-2.5"
                >
                    <div className="flex items-start gap-2">
                        <span
                            className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-semibold ${
                                status === "current"
                                    ? "sparkl-gradient text-white"
                                    : status === "completed"
                                      ? "bg-[#DCEFEC] text-[#2D7A6A]"
                                      : "bg-black/[0.04] text-[#4a5662]"
                            }`}
                        >
                            {i + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold tracking-tight truncate text-[#14202a]">
                                {s.title}
                            </p>
                            <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] mt-0.5">
                                {slideTypeLabel(s.mode)}
                                {s.estimatedSeconds
                                    ? ` · ${Math.round(s.estimatedSeconds / 60)}m`
                                    : ""}
                            </p>
                        </div>
                    </div>
                </button>

                {expanded && (
                    <div className="px-2.5 pb-2.5 pt-0.5 space-y-2 border-t border-black/[0.05]">
                        {s.notes?.length > 0 && (
                            <div className="pt-2">
                                <p className="text-[9px] uppercase tracking-[0.2em] text-[#4a5662] font-semibold mb-1.5">
                                    Talking points
                                </p>
                                <ul className="space-y-1">
                                    {s.notes.map((n, ni) => (
                                        <li
                                            key={ni}
                                            className="text-[11px] text-[#14202a] leading-snug flex gap-1.5"
                                        >
                                            <span className="text-[#5BC2CC] shrink-0 mt-0.5">
                                                ●
                                            </span>
                                            {n}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {s.misconceptions?.length > 0 && (
                            <div>
                                <p className="text-[9px] uppercase tracking-[0.2em] text-[#C75730] font-semibold mb-1.5 flex items-center gap-1">
                                    <AlertTriangle className="w-2.5 h-2.5" />
                                    Misconceptions
                                </p>
                                <ul className="space-y-1">
                                    {s.misconceptions.map((m, mi) => (
                                        <li
                                            key={mi}
                                            className="text-[11px] text-[#14202a] leading-snug px-2 py-1.5 rounded bg-[#FFF1E5] border border-[#FFA987]/20"
                                        >
                                            {m}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {help && (
                            <div
                                className="rounded-lg border border-[#5BC2CC]/40 p-2.5 relative overflow-hidden"
                                style={{
                                    background:
                                        "linear-gradient(135deg, rgba(91,194,204,0.22) 0%, rgba(91,194,204,0.10) 45%, rgba(255,169,135,0.10) 100%)",
                                }}
                            >
                                <p className="text-[9px] uppercase tracking-[0.2em] text-[#2E8E9A] font-semibold mb-2 flex items-center gap-1 relative">
                                    <Wand2 className="w-2.5 h-2.5" />
                                    AI help · how to teach this
                                </p>
                                <div className="space-y-2 relative">
                                    <div>
                                        <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-0.5">
                                            Simple explanation
                                        </p>
                                        <p className="text-[11px] text-[#14202a] leading-snug">
                                            {help.simple}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-0.5">
                                            Probing question
                                        </p>
                                        <p className="text-[11px] text-[#14202a] leading-snug">
                                            {help.probing}
                                        </p>
                                    </div>
                                    {help.analogy && (
                                        <div>
                                            <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] font-semibold mb-0.5">
                                                Analogy
                                            </p>
                                            <p className="text-[11px] text-[#14202a] italic leading-snug">
                                                {help.analogy}
                                            </p>
                                        </div>
                                    )}
                                    <PrimaryBtn
                                        testid={`teacher-help-narrate-${s.id}-btn`}
                                        onClick={() =>
                                            sendCommand("USE_AI_NARRATION", {
                                                captionOverride: help.simple,
                                            })
                                        }
                                        className="!px-2 !py-1 !text-[10px]"
                                    >
                                        <Volume2 className="w-2.5 h-2.5" />
                                        Use as narration
                                    </PrimaryBtn>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSection = (sec) => {
        const open = !!openSections[sec.id];
        const toggle = () =>
            setOpenSections((o) => ({ ...o, [sec.id]: !o[sec.id] }));
        return (
            <div
                key={sec.id}
                className="rounded-xl border border-black/[0.05] bg-white overflow-hidden"
                data-testid={`teacher-section-${sec.id}`}
            >
                <button
                    onClick={toggle}
                    className="w-full px-3 py-2 flex items-center gap-2 hover:bg-[#FAFCFD]"
                >
                    {open ? (
                        <ChevronDown className="w-3.5 h-3.5 text-[#4a5662]" />
                    ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-[#4a5662]" />
                    )}
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#14202a] font-semibold">
                        {sec.title}
                    </span>
                    <span className="ml-auto text-[9px] uppercase tracking-[0.18em] text-[#4a5662]">
                        {sec.description}
                    </span>
                </button>
                {open && (
                    <div className="p-2 space-y-1.5 border-t border-black/[0.04]">
                        {sec.id === "concept" &&
                            sec.slideIds.map((sid) => {
                                const i = findSlideIdxById(lesson.slides, sid);
                                return buildSlideCard(
                                    lesson.slides[i],
                                    i,
                                    state.slideIndex,
                                );
                            })}
                        {sec.id === "concept-check" &&
                            lesson.mcqs.map((m, i) => (
                                <button
                                    key={m.id}
                                    data-testid={`teacher-nav-mcq-${m.id}`}
                                    onClick={() => showMcq(m.id)}
                                    className={`text-left w-full p-2.5 rounded-lg border transition-all ${
                                        state.activeMcqId === m.id
                                            ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                            : "border-black/[0.05] hover:border-[#B8E8E8] bg-white"
                                    }`}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="shrink-0 w-6 h-6 rounded-md bg-[#FFF1E5] text-[#C75730] flex items-center justify-center text-[10px] font-semibold">
                                            Q{i + 1}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[12px] font-medium leading-snug text-[#14202a] line-clamp-2">
                                                {m.question}
                                            </p>
                                            <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] mt-0.5">
                                                MCQ · {m.difficulty}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        {sec.id === "practice" &&
                            lesson.subjectives.map((q, i) => (
                                <div
                                    key={q.id}
                                    className={`p-2.5 rounded-lg border ${
                                        state.activeSubjectiveId === q.id
                                            ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                            : "border-black/[0.05] bg-white"
                                    }`}
                                >
                                    <div className="flex items-start gap-2 mb-2">
                                        <span className="shrink-0 w-6 h-6 rounded-md bg-[#E0F4F4] text-[#2E8E9A] flex items-center justify-center text-[10px] font-semibold">
                                            Q{i + 1}
                                        </span>
                                        <p className="flex-1 text-[12px] font-medium leading-snug text-[#14202a] line-clamp-2">
                                            {q.question}
                                        </p>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <SoftBtn
                                            testid={`teacher-nav-solved-${q.id}`}
                                            onClick={() => applyAsSolved(q.id)}
                                            className="!flex-1 !text-[10px] !py-1.5"
                                        >
                                            Solved
                                        </SoftBtn>
                                        <SoftBtn
                                            testid={`teacher-nav-practice-${q.id}`}
                                            onClick={() => assignAsPractice(q.id)}
                                            className="!flex-1 !text-[10px] !py-1.5"
                                        >
                                            Practice
                                        </SoftBtn>
                                    </div>
                                </div>
                            ))}
                        {sec.id === "activity" && (
                            <button
                                data-testid="teacher-nav-activity"
                                onClick={startDragDrop}
                                className={`text-left w-full p-2.5 rounded-lg border transition-all ${
                                    slide.id === "drag-drop"
                                        ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                        : "border-black/[0.05] hover:border-[#B8E8E8] bg-white"
                                }`}
                            >
                                <p className="text-[12px] font-semibold text-[#14202a]">
                                    {lesson.activity.title}
                                </p>
                                <p className="text-[9px] uppercase tracking-[0.18em] text-[#4a5662] mt-0.5">
                                    Drag & drop · 3m
                                </p>
                            </button>
                        )}
                        {sec.id === "recap" &&
                            sec.slideIds.map((sid) => {
                                const i = findSlideIdxById(lesson.slides, sid);
                                return buildSlideCard(
                                    lesson.slides[i],
                                    i,
                                    state.slideIndex,
                                );
                            })}
                    </div>
                )}
            </div>
        );
    };

    /* ------------ Students seeing label ------------ */
    const studentsSeeing = useMemo(() => {
        if (!state.sessionStarted) return "Waiting screen";
        if (state.blanked) return "Blank screen";
        if (state.aiPaused) return "Paused";
        if (slide.mode === "mcq_display")
            return state.revealMcqAnswer
                ? "MCQ — answer revealed"
                : "MCQ question";
        if (slide.mode === "subjective_display")
            return state.subjectiveMode === "solved"
                ? `Solved example · step ${state.solutionStep + 1}`
                : `Practice${state.timerStartedAt ? " · timer running" : ""}`;
        if (slide.mode === "drag_drop")
            return state.activityResult === "reveal"
                ? "Activity — answer shown"
                : "Drag & drop activity";
        if (slide.mode === "blank_slide")
            return "Blank teaching slide";
        if (slide.mode === "real_life_example")
            return "Real-life example";
        return `${slideTypeLabel(slide.mode)} · fragment ${state.fragmentIndex + 1}`;
    }, [state, slide]);

    return (
        <div className="min-h-screen ambient-bg grain pb-24 lg:pr-[22rem]">
            {/* SIDE NAVIGATOR — fixed, full viewport height, overlaps header */}
            {state.sessionStarted && (
                <aside className="hidden lg:flex fixed top-0 right-0 bottom-0 w-[22rem] z-40 bg-white border-l border-black/[0.06] shadow-[-12px_0_36px_rgba(20,32,42,0.08)] flex-col">
                    <div className="px-4 py-3 border-b border-black/[0.06] flex items-center gap-2 shrink-0 bg-white/95 backdrop-blur-sm">
                        <Layers className="w-3.5 h-3.5 text-[#5BC2CC]" />
                        <p className="text-[11px] uppercase tracking-[0.22em] text-[#14202a] font-semibold">
                            Slide navigator
                        </p>
                    </div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3">
                        {lesson.sections.map((s) => renderSection(s))}
                    </div>
                </aside>
            )}
            {/* HEADER — slim: brand + session title + auto-pilot + end */}
            <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-black/[0.06]">
                <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg sparkl-gradient flex items-center justify-center">
                            <span className="text-white font-bold text-xs">S</span>
                        </div>
                        <p className="text-base font-semibold tracking-tight">
                            {lesson.sessionName}
                        </p>
                    </div>

                    <div className="ml-auto flex items-center gap-2">
                        <button
                            data-testid="teacher-toggle-autopilot-btn"
                            onClick={() =>
                                sendCommand(
                                    state.autoPilotEnabled
                                        ? "DISABLE_AUTOPILOT"
                                        : "ENABLE_AUTOPILOT",
                                    { autoPilotEnabled: !state.autoPilotEnabled },
                                )
                            }
                            disabled={!state.sessionStarted}
                            className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5 border transition-all ${
                                state.autoPilotEnabled
                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                    : "border-black/[0.06] bg-white text-[#4a5662]"
                            } disabled:opacity-40`}
                        >
                            <Zap className="w-3 h-3" />
                            Auto-pilot {state.autoPilotEnabled ? "On" : "Off"}
                        </button>
                        <button
                            data-testid="teacher-ai-mic-btn"
                            onClick={() => setIsVoiceModalOpen(true)}
                            disabled={!state.sessionStarted}
                            className="relative group px-3.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold text-white flex items-center gap-1.5 transition-all active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none"
                            style={{
                                background: "linear-gradient(135deg, #7C6BF2, #5BC2CC, #FFA987)",
                                boxShadow: "0 4px 14px rgba(124,107,242,0.4)"
                            }}
                            title="Speak to the classroom AI co-teacher"
                        >
                            <Mic className="w-3.5 h-3.5" />
                            Ask AI
                        </button>
                        <button
                            data-testid="teacher-fullscreen-student-btn"
                            onClick={requestStudentFullscreen}
                            disabled={!state.sessionStarted}
                            className="p-2 rounded-lg transition-all border-2 border-black/[0.06] bg-white hover:border-[#5BC2CC] disabled:opacity-40"
                            title="Make student screen fullscreen"
                        >
                            <Maximize2 className="w-3.5 h-3.5 text-[#4a5662]" />
                        </button>
                        {joinCode && (
                            <div
                                data-testid="teacher-photo-join-code"
                                className="px-3 py-1.5 rounded-xl bg-[#14202a] text-white flex items-center gap-2"
                                title="Open the Sparkl Teacher app on your phone and enter this code to control from your phone"
                            >
                                <span className="text-[9px] uppercase tracking-[0.22em] text-[#B8E8E8] font-semibold">
                                    Phone code
                                </span>
                                <span className="text-[13px] font-bold tracking-[0.2em]">
                                    {joinCode}
                                </span>
                            </div>
                        )}
                        <button
                            data-testid="teacher-toggle-blank-btn"
                            onClick={toggleBlank}
                            disabled={!state.sessionStarted}
                            className={`p-2 rounded-lg transition-all border-2 ${
                                state.blanked
                                    ? "border-[#5BC2CC] bg-[#E0F4F4]"
                                    : "border-black/[0.06] bg-white hover:border-[#B8E8E8]"
                            } disabled:opacity-40`}
                            title={state.blanked ? "Restore" : "Blank"}
                        >
                            {state.blanked ? (
                                <Eye className="w-3.5 h-3.5 text-[#2E8E9A]" />
                            ) : (
                                <EyeOff className="w-3.5 h-3.5 text-[#4a5662]" />
                            )}
                        </button>
                        <button
                            data-testid="teacher-end-session-btn"
                            onClick={endSession}
                            disabled={!state.sessionStarted}
                            className="px-3 py-2 rounded-lg bg-white border-2 border-red-100 text-red-600 hover:bg-red-50 active:scale-[0.97] transition-all flex items-center gap-1.5 text-xs font-semibold disabled:opacity-40"
                        >
                            <Power className="w-3.5 h-3.5" />
                            End
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1700px] mx-auto px-5 py-5">
                {!state.sessionStarted ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#4a5662] mb-4">
                            Ready
                        </p>
                        <h2 className="text-4xl font-semibold tracking-tight mb-3">
                            Start the lesson on photosynthesis
                        </h2>
                        <p className="text-[#4a5662] mb-8 max-w-md">
                            Open <span className="font-semibold">/student</span>{" "}
                            on the smart-board, then start here.
                        </p>
                        <button
                            data-testid="teacher-start-session-btn"
                            onClick={startSession}
                            className="px-8 py-4 rounded-2xl sparkl-gradient text-white font-semibold text-base shadow-[0_8px_24px_rgba(91,194,204,0.35)] hover:shadow-[0_12px_30px_rgba(91,194,204,0.45)] active:scale-[0.98] transition-all flex items-center gap-2"
                        >
                            <Play className="w-5 h-5" />
                            Start Session
                        </button>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* MAIN */}
                        <div className="space-y-5">
                            {/* Live preview with vertical annotate rail on the left */}
                            <div className="rounded-3xl bg-white border border-black/[0.05] soft-shadow overflow-hidden relative">
                                <div className="px-5 py-2.5 flex items-center justify-between border-b border-black/[0.04]">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5 text-[#5BC2CC]" />
                                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold">
                                            Student display
                                        </span>
                                    </div>
                                    {/* Play / Full-render toggle */}
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            data-testid="teacher-render-play-btn"
                                            onClick={() =>
                                                sendCommand("SET_RENDER_PLAY", {
                                                    slideRenderMode: "play",
                                                    fragmentIndex: 0,
                                                })
                                            }
                                            disabled={state.slideSelected === false}
                                            className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1 border transition-all ${
                                                state.slideRenderMode === "play"
                                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                                    : "border-black/[0.06] bg-white text-[#4a5662]"
                                            } disabled:opacity-40`}
                                        >
                                            <Play className="w-2.5 h-2.5" />
                                            Play
                                        </button>
                                        <button
                                            data-testid="teacher-render-full-btn"
                                            onClick={() =>
                                                sendCommand("SET_RENDER_FULL", {
                                                    slideRenderMode: "full",
                                                })
                                            }
                                            disabled={state.slideSelected === false}
                                            className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1 border transition-all ${
                                                state.slideRenderMode === "full"
                                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                                    : "border-black/[0.06] bg-white text-[#4a5662]"
                                            } disabled:opacity-40`}
                                        >
                                            <Layers className="w-2.5 h-2.5" />
                                            Full
                                        </button>
                                    </div>
                                </div>
                                <div className="flex">
                                    {/* Vertical annotate rail + Blank slide FAB */}
                                    <div className="shrink-0 w-14 bg-[#FAFCFD] border-r border-black/[0.04] flex flex-col items-center py-3 gap-1.5">
                                        <button
                                            data-testid="teacher-blank-fab-btn"
                                            onClick={() => addBlankSlide("white")}
                                            title="Insert blank teaching slide"
                                            className="w-10 h-10 rounded-full sparkl-gradient text-white flex items-center justify-center shadow-[0_4px_14px_rgba(91,194,204,0.35)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.45)] active:scale-95 transition-all mb-2"
                                        >
                                            <PlusCircle className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid="teacher-tool-off-btn"
                                            onClick={() => setTool("off")}
                                            title="Cursor"
                                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 ${
                                                state.annotationTool === "off"
                                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                                    : "border-transparent text-[#4a5662] hover:bg-white"
                                            }`}
                                        >
                                            <MousePointer2 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            data-testid="teacher-tool-pen-btn"
                                            onClick={() => setTool("pen")}
                                            title="Pen"
                                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 ${
                                                state.annotationTool === "pen"
                                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                                    : "border-transparent text-[#4a5662] hover:bg-white"
                                            }`}
                                        >
                                            <Pen className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            data-testid="teacher-tool-highlight-btn"
                                            onClick={() => setTool("highlight")}
                                            title="Highlight"
                                            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 ${
                                                state.annotationTool === "highlight"
                                                    ? "border-[#5BC2CC] bg-[#E0F4F4] text-[#2E8E9A]"
                                                    : "border-transparent text-[#4a5662] hover:bg-white"
                                            }`}
                                        >
                                            <Highlighter className="w-3.5 h-3.5" />
                                        </button>
                                        <div className="w-7 h-px bg-black/10 my-1" />
                                        {[
                                            { c: "#2E8E9A", t: "aqua" },
                                            { c: "#FF9A78", t: "peach" },
                                            { c: "#14202a", t: "ink" },
                                        ].map(({ c, t }) => (
                                            <button
                                                key={c}
                                                data-testid={`teacher-color-${t}-btn`}
                                                onClick={() => setColor(c)}
                                                title={t}
                                                className={`w-6 h-6 rounded-full border-2 transition-all ${
                                                    state.annotationColor === c
                                                        ? "border-[#14202a] scale-110"
                                                        : "border-white shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                                                }`}
                                                style={{ backgroundColor: c }}
                                            />
                                        ))}
                                        <div className="w-7 h-px bg-black/10 my-1" />
                                        <button
                                            data-testid="teacher-undo-stroke-btn"
                                            onClick={undoStroke}
                                            title="Undo last stroke"
                                            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 border-transparent text-[#4a5662] hover:bg-white"
                                        >
                                            <RotateCcw className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            data-testid="teacher-clear-annotations-btn"
                                            onClick={clearAnnotations}
                                            title="Clear annotations"
                                            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 border-transparent text-[#4a5662] hover:bg-white"
                                        >
                                            <Eraser className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {/* Stage */}
                                    <div
                                        className="relative flex-1 bg-[#F5FAFC]"
                                        style={{ aspectRatio: "16 / 9" }}
                                        data-testid="teacher-preview-stage"
                                    >
                                        <LessonStage
                                            state={state}
                                            showChrome
                                            annotationProps={{
                                                interactive: true,
                                                tool: state.annotationTool,
                                                color: state.annotationColor,
                                                onAddStroke: addStroke,
                                            }}
                                            dragDropProps={{
                                                interactive: true,
                                                onPlace: placeItem,
                                                onPickup: pickupItem,
                                            }}
                                            onExamplePopupClose={closeExamplePopupForStudents}
                                        />
                                    </div>
                                </div>
                                {/* Slide-specific action bar */}
                                <SlideActionBar />
                            </div>

                            {/* Real-life examples — cards below the slide preview */}
                            <RealLifeExamplesGrid />
                        </div>
                    </div>
                )}
            </div>

            {/* FLOATING DOCK — centered, frosted glass, icon-only nav */}
            {state.sessionStarted && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30">
                    <div className="bg-white/75 backdrop-blur-xl border border-white/60 rounded-2xl shadow-[0_18px_48px_rgba(20,32,42,0.18)] px-2.5 py-1.5 flex items-center gap-1">
                        {/* Slide nav: prev | pause/play | next | replay */}
                        <button
                            data-testid="teacher-prev-slide-btn"
                            onClick={prevSlide}
                            disabled={state.slideIndex <= 0}
                            title="Previous slide"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <SkipBack className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="teacher-pause-btn"
                            onClick={togglePause}
                            title={state.aiPaused ? "Resume teaching" : "Pause teaching"}
                            className="w-10 h-10 rounded-xl sparkl-gradient text-white flex items-center justify-center shadow-[0_4px_14px_rgba(91,194,204,0.35)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.45)] active:scale-95 transition-all"
                        >
                            {state.aiPaused ? (
                                <Play className="w-4 h-4 pl-0.5" />
                            ) : (
                                <Pause className="w-4 h-4" />
                            )}
                        </button>
                        <button
                            data-testid="teacher-next-slide-btn"
                            onClick={nextSlide}
                            disabled={state.slideIndex >= lesson.slides.length - 1}
                            title="Next slide"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <SkipForward className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="teacher-replay-slide-btn"
                            onClick={replaySlide}
                            title="Replay slide narration"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>

                        <div className="h-6 w-px bg-black/10 mx-1" />

                        {/* Fragment nav: prev | counter | next */}
                        <button
                            data-testid="teacher-prev-fragment-btn"
                            onClick={prevFragment}
                            disabled={state.fragmentIndex <= 0}
                            title="Previous fragment"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <Rewind className="w-4 h-4" />
                        </button>
                        <span className="text-[10px] text-[#4a5662] tabular-nums px-1 font-semibold">
                            {state.fragmentIndex + 1}/{totalFragments || 1}
                        </span>
                        <button
                            data-testid="teacher-next-fragment-btn"
                            onClick={nextFragment}
                            disabled={state.fragmentIndex >= totalFragments - 1}
                            title="Next fragment"
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-[#14202a] hover:bg-black/[0.05] active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <Forward className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
            <VoiceAssistantModal
                isOpen={isVoiceModalOpen}
                onClose={() => setIsVoiceModalOpen(false)}
                sessionChannel={sessionChannel}
                activeLesson={lesson}
                slideIndex={state.slideIndex}
                state={state}
                sendCommand={sendCommand}
            />

            {/* Example PREVIEW modal (teacher-only) — preview, then "Show students" broadcasts as popup */}
            <AnimatePresence>
                {previewExample && (
                    <motion.div
                        key={previewExample.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-[#14202a]/60 backdrop-blur-md"
                        data-testid="teacher-example-preview-modal"
                        onClick={() => setPreviewExampleId(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-3xl rounded-3xl bg-white soft-shadow overflow-hidden"
                        >
                            <div className="absolute top-4 left-4 z-10">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFF1E5] text-[#C75730] text-[10px] uppercase tracking-[0.22em] font-semibold">
                                    Real-life example · preview
                                </span>
                            </div>
                            <button
                                onClick={() => setPreviewExampleId(null)}
                                data-testid="teacher-example-preview-close"
                                className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/10 text-[#14202a] flex items-center justify-center text-xl"
                            >
                                ×
                            </button>
                            <div className="aspect-[16/9] overflow-hidden">
                                <img
                                    src={previewExample.image}
                                    alt={previewExample.headline || previewExample.text}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-[#4a5662] font-semibold mb-2">
                                    Real-life example
                                </p>
                                <h3 className="text-2xl font-semibold tracking-tight mb-2">
                                    {previewExample.headline || previewExample.text}
                                </h3>
                                <p className="text-sm text-[#4a5662] leading-relaxed mb-5">
                                    {previewExample.description || previewExample.text}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid="teacher-show-example-students-btn"
                                        onClick={() => showExampleToStudents(previewExample.id)}
                                        className="px-4 py-2 rounded-xl sparkl-gradient text-white font-semibold text-sm shadow-[0_4px_14px_rgba(91,194,204,0.3)] hover:shadow-[0_6px_18px_rgba(91,194,204,0.4)] active:scale-[0.98] transition-all flex items-center gap-1.5"
                                    >
                                        <Eye className="w-3.5 h-3.5" />
                                        Show to students
                                    </button>
                                    <button
                                        onClick={() => setPreviewExampleId(null)}
                                        className="px-4 py-2 rounded-xl bg-black/[0.04] text-[#4a5662] font-semibold text-sm hover:bg-black/[0.07] active:scale-[0.98] transition-all"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating AI recommendation card — fixed bottom-right, animated glow */}
            <AnimatePresence mode="wait">
                {state.sessionStarted && activeRec && (
                    <motion.div
                        key={activeRec.id}
                        data-testid="floating-rec-card"
                        initial={{ x: 120, opacity: 0, scale: 0.96 }}
                        animate={{
                            x: 0,
                            opacity: 1,
                            scale: 1,
                            boxShadow: [
                                "0 8px 24px rgba(20,32,42,0.10), 0 0 0 0 rgba(91,194,204,0.0)",
                                "0 14px 36px rgba(20,32,42,0.14), 0 0 28px 6px rgba(91,194,204,0.45)",
                                "0 8px 24px rgba(20,32,42,0.10), 0 0 0 0 rgba(91,194,204,0.0)",
                            ],
                        }}
                        exit={{ x: 120, opacity: 0, scale: 0.96 }}
                        transition={{
                            x: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
                            opacity: { duration: 0.4 },
                            scale: { duration: 0.45 },
                            boxShadow: {
                                duration: 3.4,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: 0.5,
                            },
                        }}
                        className="fixed right-6 z-40 max-w-sm p-4 rounded-2xl bg-white border border-[#5BC2CC]/40 overflow-hidden"
                        style={{ bottom: "84px" }}
                    >
                        {/* Animated aqua aura behind */}
                        <motion.div
                            aria-hidden
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0.25, 0.55, 0.25] }}
                            transition={{
                                duration: 3.4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="absolute -inset-1 rounded-2xl pointer-events-none"
                            style={{
                                background:
                                    "radial-gradient(120% 80% at 100% 0%, rgba(91,194,204,0.35) 0%, rgba(91,194,204,0) 60%)",
                            }}
                        />
                        <div className="relative flex items-start gap-2.5">
                            <motion.div
                                animate={{
                                    boxShadow: [
                                        "0 0 0 0 rgba(91,194,204,0)",
                                        "0 0 18px 4px rgba(91,194,204,0.55)",
                                        "0 0 0 0 rgba(91,194,204,0)",
                                    ],
                                }}
                                transition={{
                                    duration: 2.4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="w-8 h-8 rounded-lg sparkl-gradient flex items-center justify-center shrink-0"
                            >
                                <Sparkles className="w-3.5 h-3.5 text-white" />
                            </motion.div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#2E8E9A] font-semibold mb-1">
                                    AI suggestion
                                </p>
                                <p className="text-sm text-[#14202a] leading-snug mb-3">
                                    {activeRec.text}
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        data-testid={`floating-rec-show-${activeRec.id}`}
                                        onClick={() => {
                                            if (activeRec.action === "show-mcq")
                                                showMcq(activeRec.target);
                                            else if (
                                                activeRec.action === "use-solved"
                                            )
                                                applyAsSolved(activeRec.target);
                                            else if (
                                                activeRec.action ===
                                                "assign-practice"
                                            )
                                                assignAsPractice(activeRec.target);
                                            else if (
                                                activeRec.action ===
                                                "start-activity"
                                            )
                                                startDragDrop();
                                            else if (
                                                activeRec.action === "show-example"
                                            )
                                                showExample(activeRec.target);
                                            else if (
                                                activeRec.action === "show-help"
                                            )
                                                showHelp(activeRec.target);
                                            else if (
                                                activeRec.action === "annotate"
                                            )
                                                setTool("pen");
                                            else if (activeRec.action === "pause")
                                                togglePause();
                                            else if (
                                                activeRec.action ===
                                                "show-activity-answer"
                                            )
                                                showActivityAnswer();
                                        }}
                                        className="px-3 py-1.5 rounded-lg sparkl-gradient text-white text-xs font-semibold"
                                    >
                                        Show this
                                    </button>
                                    <button
                                        data-testid={`floating-rec-dismiss-${activeRec.id}`}
                                        onClick={() => dismissRec(activeRec.id)}
                                        className="px-3 py-1.5 rounded-lg bg-black/[0.04] text-[#4a5662] text-xs font-semibold"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/**
 * Routes to the deck.js shell for external lessons (e.g. gaseous-state)
 * and keeps the photosynthesis-specific React panel untouched otherwise.
 */
const TeacherPanelRouter = () => {
    const lessonId = new URLSearchParams(window.location.search).get(
        "lessonId",
    );
    // Resolve active lesson BEFORE rendering — the `lesson` Proxy reads
    // from the registry at access time, so this controls which lesson
    // TeacherPanel / LessonStage render.
    if (lessonId) setActiveLessonId(lessonId);
    if (lessonId === "gaseous-state") return <TeacherPanelDeck />;
    return <TeacherPanel />;
};

export default TeacherPanelRouter;
